import type { EntryDto, EntryResponse } from "src/models/sessions";

const KEY = "pft.station.outbox";
export const BATCH_SIZE = 200;

export interface QueuedEntry {
  sessionId: string;
  entry: EntryDto;
  queuedAt: string;
  attempts: number;
  lastError?: string;
}

const read = (): Record<string, QueuedEntry> => {
  try {
    return JSON.parse(window.localStorage.getItem(KEY) || "{}");
  } catch {
    return {};
  }
};
const write = (items: Record<string, QueuedEntry>) => {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(items));
  } catch {
    /* quota or private mode: entries stay in memory for this page only */
  }
};

/**
 * Browser outbox for the web station: entries are keyed by their client UUID (idempotent), kept
 * in localStorage across reloads, and drained oldest-first whenever the browser is online.
 */
export const outbox = {
  all: (): QueuedEntry[] =>
    Object.values(read()).sort((a, b) => a.queuedAt.localeCompare(b.queuedAt)),
  count: () => Object.keys(read()).length,
  enqueue(sessionId: string, entry: EntryDto) {
    const items = read();
    items[entry.id] = {
      sessionId,
      entry,
      queuedAt: new Date().toISOString(),
      attempts: 0,
    };
    write(items);
  },
  remove(ids: string[]) {
    const items = read();
    for (const id of ids) delete items[id];
    write(items);
  },
  markFailed(ids: string[], error: string) {
    const items = read();
    for (const id of ids)
      if (items[id])
        items[id] = {
          ...items[id],
          attempts: items[id].attempts + 1,
          lastError: error,
        };
    write(items);
  },
  purgeSession(sessionId: string) {
    const items = read();
    for (const [id, q] of Object.entries(items))
      if (q.sessionId === sessionId) delete items[id];
    write(items);
  },
  clear() {
    write({});
  },
};

/** The refusal grading returns for score entry while the district's license is suspended (R19). */
export const LICENSE_SUSPENDED = "LICENSE_SUSPENDED";

/** True when a send failed because the district's license is suspended (`{ code }` or `{ data: { code } }`). */
export const isLicenseSuspended = (error: unknown) => {
  const data = (
    error as { data?: { code?: unknown; data?: { code?: unknown } } }
  )?.data;
  return (data?.code ?? data?.data?.code) === LICENSE_SUSPENDED;
};

/**
 * Sends the outbox in per-session batches. `send` performs one batch request and returns the
 * per-entry results; rejected entries are dropped, transport failures are retried later. A
 * license-suspended refusal keeps the entries and rethrows, so the station can say it is paused.
 */
export async function drain(
  send: (sessionId: string, entries: EntryDto[]) => Promise<EntryResponse[]>,
): Promise<{ sent: number; failed: number }> {
  if (typeof navigator !== "undefined" && navigator.onLine === false)
    return { sent: 0, failed: 0 };
  const items = outbox.all();
  let sent = 0;
  let failed = 0;
  const bySession: Record<string, QueuedEntry[]> = {};
  for (const q of items)
    bySession[q.sessionId] = [...(bySession[q.sessionId] ?? []), q];
  for (const sessionId of Object.keys(bySession)) {
    const group = bySession[sessionId];
    for (let i = 0; i < group.length; i += BATCH_SIZE) {
      const chunk = group.slice(i, i + BATCH_SIZE);
      try {
        const results = await send(
          sessionId,
          chunk.map((q: QueuedEntry) => q.entry),
        );
        // Stored and superseded entries are done; rejected ones never succeed on retry.
        outbox.remove(results.map((r) => r.id));
        sent += results.length;
      } catch (error: any) {
        const status = error?.status;
        if (isLicenseSuspended(error)) {
          // FR-043: the entries are kept, so they sync if the license is reinstated.
          outbox.markFailed(
            chunk.map((q: QueuedEntry) => q.entry.id),
            LICENSE_SUSPENDED,
          );
          throw error;
        }
        if (status === 403 || status === 409 || status === 401) {
          outbox.purgeSession(sessionId);
          throw error; // the session has closed for this scorer
        }
        outbox.markFailed(
          chunk.map((q: QueuedEntry) => q.entry.id),
          error?.data?.message ?? String(status ?? "network"),
        );
        failed += chunk.length;
      }
    }
  }
  return { sent, failed };
}
